import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDeleteInventoryAirlineBaggageRule } from '@/api/generated/inventory-airlines/inventory-airlines';
import type { AirlineBaggageRuleItem } from '@/api/generated/schemas';
import { Alert, Badge, Button, ConfirmDialog, EmptyState, Panel, Table, toast } from '@/shared/ui';
import { invalidateAirline } from '../../internal/airlinesQueryKeys';
import {
  baggagePurchaseChannelsOf,
  emptyBaggageRuleFormValues,
  fromAirlineBaggageRuleItem,
  type BaggageRuleFormValues,
} from '../baggageRuleFormSchema';
import { BaggageRuleDialog } from './BaggageRuleDialog';

/** Měna dopravce tak, jak je **uložená** — id pro srovnání s formulářem, kód pro zobrazení částek. */
export interface CarrierCurrency {
  id: number;
  code: string;
}

interface AirlineBaggagePanelProps {
  /** Bez `airlineId` je dopravce v režimu zakládání — pravidla nemají na čem viset. */
  airlineId?: number;
  rules: AirlineBaggageRuleItem[];
  carrierCurrency: CarrierCurrency | null;
  /** Měna v hlavním formuláři se liší od uložené — dokud se neuloží, pravidlo se zapsat nesmí. */
  currencyUnsaved: boolean;
}

/** Rozměry pravidla; nevyplněná trojice znamená „dle typu". */
function formatDimensions(t: TFunction, rule: AirlineBaggageRuleItem): string {
  const { widthCm: w, heightCm: h, depthCm: d } = rule;
  return w !== null && h !== null && d !== null
    ? t('baggage.dimensions.value', { width: w, height: h, depth: d })
    : t('baggage.dimensions.byType');
}

/** Orientační cena v měně dopravce; pravidlo v ceně letenky žádnou nemá. */
function formatPrice(
  t: TFunction,
  rule: AirlineBaggageRuleItem,
  currency: CarrierCurrency | null
): string {
  if (rule.isIncluded) {
    return t('baggage.price.free');
  }
  const { priceFrom: from, priceTo: to } = rule;
  if (from === null && to === null) {
    return '—';
  }
  const code = currency?.code ?? '';
  return from !== null && to !== null && from !== to
    ? t('baggage.price.range', { from, to, currency: code })
    : t('baggage.price.single', { value: from ?? to, currency: code });
}

/** Kanály nákupu jako čárkou oddělený výčet; alespoň jeden je vždy vybraný. */
function formatChannels(t: TFunction, rule: AirlineBaggageRuleItem): string {
  const labels = baggagePurchaseChannelsOf(rule).map((channel) =>
    t(`baggagePurchaseChannel.${channel}`)
  );
  return labels.length > 0 ? labels.join(', ') : '—';
}

/**
 * Sekce „Baggage" — tabulka pravidel dopravce s vlastním CRUD. Pravidla se ukládají **okamžitě
 * vlastními endpointy**, nezávisle na formuláři letecké společnosti (stejně jako dokumenty vozu),
 * takže se jich ukládací lišta detailu netýká.
 */
export function AirlineBaggagePanel({
  airlineId,
  rules,
  carrierCurrency,
  currencyUnsaved,
}: AirlineBaggagePanelProps) {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BaggageRuleFormValues | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AirlineBaggageRuleItem | null>(null);

  const deleteMutation = useDeleteInventoryAirlineBaggageRule({
    mutation: {
      onSuccess: () => {
        if (airlineId != null) {
          invalidateAirline(queryClient, airlineId);
        }
        toast({ severity: 'success', description: t('baggage.toast.deleted') });
      },
      onError: () =>
        toast({
          severity: 'error',
          title: t('baggage.toast.errorTitle'),
          description: t('baggage.toast.deleteError'),
        }),
    },
  });

  return (
    <>
      <Panel
        variant="filled"
        icon="tag"
        title={t('baggage.title')}
        actions={
          airlineId != null ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconLeft="plus"
              disabled={currencyUnsaved}
              onClick={() => setDraft(emptyBaggageRuleFormValues())}
            >
              {t('baggage.add')}
            </Button>
          ) : null
        }
      >
        {airlineId == null ? (
          // Pravidlo visí na ID dopravce, takže při zakládání nemá kam patřit — stejně jako
          // záložka Dokumenty u vozu.
          <EmptyState
            icon="tag"
            title={t('baggage.saveAirlineFirstTitle')}
            description={t('baggage.saveAirlineFirstDescription')}
          />
        ) : (
          <div className="gap-phx-3 flex flex-col">
            {currencyUnsaved ? (
              <Alert severity="warning" data-testid="baggage-currency-unsaved">
                {t('baggage.currencyUnsaved')}
              </Alert>
            ) : null}

            {rules.length === 0 ? (
              <EmptyState icon="tag" title={t('baggage.empty')} />
            ) : (
              <div className="border-phx-border rounded-phx overflow-x-auto border">
                <Table density="dense">
                  <Table.Head>
                    <Table.Row>
                      <Table.HCell density="dense">{t('baggage.cols.category')}</Table.HCell>
                      <Table.HCell density="dense">{t('baggage.cols.name')}</Table.HCell>
                      <Table.HCell density="dense">{t('baggage.cols.dimensions')}</Table.HCell>
                      <Table.HCell density="dense">{t('baggage.cols.weight')}</Table.HCell>
                      <Table.HCell density="dense">{t('baggage.cols.pricing')}</Table.HCell>
                      <Table.HCell density="dense">{t('baggage.cols.maxPieces')}</Table.HCell>
                      <Table.HCell density="dense">{t('baggage.cols.channels')}</Table.HCell>
                      <Table.HCell density="dense">{t('baggage.cols.price')}</Table.HCell>
                      <Table.HCell density="dense" className="w-20" />
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {rules.map((rule) => (
                      <Table.Row key={rule.id}>
                        <Table.Cell density="dense">
                          {t(`baggageCategory.${rule.category}`)}
                        </Table.Cell>
                        <Table.Cell density="dense">
                          <span className="font-medium">{rule.name}</span>
                          {rule.description ? (
                            <span className="text-phx-text-muted text-phx-xs block">
                              {rule.description}
                            </span>
                          ) : null}
                        </Table.Cell>
                        <Table.Cell density="dense" className="font-mono whitespace-nowrap">
                          {formatDimensions(t, rule)}
                        </Table.Cell>
                        <Table.Cell density="dense" className="font-mono whitespace-nowrap">
                          {rule.weightKg === null
                            ? '—'
                            : t('baggage.weight.value', { value: rule.weightKg })}
                        </Table.Cell>
                        <Table.Cell density="dense">
                          <Badge tone={rule.isIncluded ? 'success' : 'neutral'}>
                            {t(
                              rule.isIncluded ? 'baggagePricing.Included' : 'baggagePricing.Extra'
                            )}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell density="dense" className="tabular-nums">
                          {rule.maxPiecesPerPassenger}
                        </Table.Cell>
                        <Table.Cell density="dense">{formatChannels(t, rule)}</Table.Cell>
                        <Table.Cell density="dense" className="whitespace-nowrap">
                          {formatPrice(t, rule, carrierCurrency)}
                        </Table.Cell>
                        <Table.Cell density="dense" className="text-right whitespace-nowrap">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            iconLeft="edit"
                            disabled={currencyUnsaved}
                            aria-label={t('baggage.edit')}
                            onClick={() => setDraft(fromAirlineBaggageRuleItem(rule))}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            iconLeft="trash"
                            aria-label={t('baggage.remove')}
                            onClick={() => setPendingDelete(rule)}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>
        )}
      </Panel>

      {draft && airlineId != null ? (
        <BaggageRuleDialog
          airlineId={airlineId}
          initialValues={draft}
          carrierCurrency={carrierCurrency}
          onClose={() => setDraft(null)}
        />
      ) : null}

      {pendingDelete && airlineId != null ? (
        <ConfirmDialog
          title={t('baggage.confirmDelete.title')}
          message={t('baggage.confirmDelete.message', { name: pendingDelete.name })}
          onConfirm={() => {
            deleteMutation.mutate({ id: airlineId, ruleId: pendingDelete.id });
            setPendingDelete(null);
          }}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </>
  );
}
